import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export interface Client {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    address?: string;
    tax_id?: string;
    status: "active" | "inactive";
    created_at: string;
}

export const parseLocalDate = (dateInput: string | Date | undefined): Date => {
    if (!dateInput) return new Date();
    if (dateInput instanceof Date) return dateInput;

    const str = String(dateInput).trim();
    const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
        const year = parseInt(match[1], 10);
        const month = parseInt(match[2], 10) - 1;
        const day = parseInt(match[3], 10);
        return new Date(year, month, day, 12, 0, 0);
    }
    return new Date(str);
};

export const isUrgentDueDate = (dueDateStr?: string): boolean => {
    if (!dueDateStr) return false;
    const due = parseLocalDate(dueDateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 3;
};

export interface Contact {
    id: string;
    name: string;
    role?: string;
    company?: string;
    email?: string;
    phone?: string;
}

export interface Tramite {
    id: string;
    title: string;
    entity?: string;
    responsible?: string;
    status: "pending" | "in_progress" | "completed" | "cancelled";
    due_date?: string;
    description?: string;
}

export interface Receipt {
    id: string;
    receipt_number?: string;
    client_id?: string;
    client_name?: string;
    date: string;
    amount: number;
    concept?: string;
    status: "paid" | "pending" | "cancelled";
}

// Re-using types from legal assistant if possible, or defining simplified ones here
export interface Process {
    id: string;
    case_number: string;
    title: string;
    description?: string;
    status: "active" | "inactive" | "closed" | "archived" | string;
    client_name?: string;
    client_email?: string;
    client_phone?: string;
    client_role?: string;
    updated_at?: string;
    created_at?: string;
    radicado?: string;
    juzgado?: string;
    tags?: string[];
    grupos?: string[];
    user_id?: string;
    ponente?: string;
    tipo_proceso?: string;
    clase?: string;
    last_change_at?: string;
    ubicacion?: string;
    despacho?: string;
    demandante?: string;
    demandado?: string;
    ciudad?: string;
    direccion?: string;
    jurisdiccion?: string;
    cuantia?: string;
    fechaInicio?: string;
    fechaRadicacion?: string;
    instancia?: string;
    subclase?: string;
    recurso?: string;
    naturaleza?: string;
    medidaCautelar?: string;
    vigente?: string;
    metadata?: Record<string, any>;
}

export interface Expense {
    id: string;
    case_id: string;
    title: string;
    amount: number;
    date: string;
    is_reimbursable: boolean;
    status: string;
}

export interface Deadline {
    id: string;
    case_id?: string | null;
    title: string;
    description?: string;
    due_date: string;
    status: string;
    reminder_days?: number;
    created_at?: string;
}

export function useDespachos() {
    const { user } = useAuth();

    const [clients, setClients] = useState<Client[]>([]);
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [tramites, setTramites] = useState<Tramite[]>([]);
    const [receipts, setReceipts] = useState<Receipt[]>([]);
    const [processes, setProcesses] = useState<Process[]>([]);
    const [expenses, setExpenses] = useState<Expense[]>([]); 
    const [deadlines, setDeadlines] = useState<Deadline[]>([]);
    const [loading, setLoading] = useState(true);

    const [dashboardMetrics, setDashboardMetrics] = useState({
        activeProcesses: 0,
        totalClients: 0,
        pendingTramites: 0,
        urgentTasksCount: 0,
        recentActivity: 0
    });

    const fetchData = async () => {
        if (!user) return;
        setLoading(true);
        try {
            // Fetch Clients
            const { data: clientsData, error: clientsError } = await supabase
                .from("despachos_clients")
                .select("*")
                .eq("user_id", user.id)
                .order("created_at", { ascending: false });
            if (clientsError) throw clientsError;
            setClients(clientsData || []);

            // Fetch Contacts (New)
            const { data: contactsData, error: contactsError } = await supabase
                .from("despachos_contacts")
                .select("*")
                .eq("user_id", user.id)
                .order("name", { ascending: true });
            if (contactsError && contactsError.code !== '42P01') { // Ignore if table doesn't exist yet
                console.error("Error fetching contacts:", contactsError);
            }
            setContacts(contactsData || []);

            // Fetch Tramites
            const { data: tramitesData, error: tramitesError } = await supabase
                .from("despachos_tramites")
                .select("*")
                .eq("user_id", user.id)
                .order("due_date", { ascending: true });
            if (tramitesError) throw tramitesError;
            setTramites(tramitesData || []);

            // Fetch Receipts
            const { data: receiptsData, error: receiptsError } = await supabase
                .from("despachos_receipts")
                .select("*")
                .eq("user_id", user.id)
                .order("date", { ascending: false });
            if (receiptsError) throw receiptsError;
            setReceipts(receiptsData || []);

            // 5. Existing Processes (procesos)
            const { data: processesData, error: processesError } = await supabase
                .from("procesos")
                .select("*")
                .eq("user_id", user.id)
                .order("created_at", { ascending: false });
            if (processesError) throw processesError;
            
            const mappedProcesses = (processesData || []).map((p: any) => {
                const meta = p.metadata || {};
                const demAct = meta.demandante || p.demandante || '';
                const demPas = meta.demandado || p.demandado || '';
                const ubicacionVal = meta.ubicacion || meta.juzgado || p.ubicacion || '';
                let computedTitle = p.asunto || meta.asunto || '';

                if (!computedTitle && (demAct || demPas)) {
                    if (demAct && demPas) {
                        computedTitle = `Demandante: ${demAct} | DEMANDADO: ${demPas}`;
                    } else if (demAct) {
                        computedTitle = `Demandante: ${demAct}`;
                    } else {
                        computedTitle = `DEMANDADO: ${demPas}`;
                    }
                }
                if (!computedTitle) {
                    computedTitle = p.clase || meta.clase || 'Proceso sin título';
                }

                const computedStatus = (demAct || demPas || ubicacionVal)
                    ? 'updated'
                    : (p.watch_status || (p.is_watched ? 'active' : 'inactive'));

                // Unify tags and grupos: eliminate redundancy while preserving legacy user data
                const rawTags = Array.isArray(meta.tags) ? meta.tags : [];
                const rawGrupos = Array.isArray(meta.grupos) ? meta.grupos : [];
                const unifiedTags = Array.from(new Set([...rawTags, ...rawGrupos]))
                    .filter((t: any) => typeof t === "string" && t.trim() !== "");

                return {
                    id: p.id,
                    case_number: p.numero_proceso,
                    title: computedTitle,
                    description: meta.description || p.tipo_proceso || meta.tipo_proceso || '',
                    status: computedStatus,
                    client_name: meta.client_name || '',
                    client_email: meta.client_email || '',
                    client_phone: meta.client_phone || '',
                    client_role: meta.client_role || 'Demandante',
                    updated_at: p.last_check_at || p.last_sync_at || p.created_at,
                    radicado: p.numero_proceso,
                    juzgado: ubicacionVal,
                    jurisdiccion: meta.jurisdiccion || '',
                    cuantia: meta.cuantia || '',
                    fechaInicio: meta.fecha_inicio || '',
                    fechaRadicacion: meta.fecha_radicacion || '',
                    instancia: meta.instancia || 'primera',
                    tags: unifiedTags,
                    grupos: unifiedTags,
                    user_id: p.user_id,
                    ponente: p.ponente || meta.ponente || '',
                    tipo_proceso: p.tipo_proceso || meta.tipo_proceso || '',
                    clase: p.clase || meta.clase || '',
                    subclase: meta.subclase || '',
                    recurso: meta.recurso || '',
                    naturaleza: meta.naturaleza || '',
                    medidaCautelar: meta.medida_cautelar || '',
                    vigente: p.vigente || meta.vigente || meta.es_privado || p.estado || 'VIGENTE',
                    last_change_at: p.last_change_at || '',
                    metadata: meta
                };
            });
            setProcesses(mappedProcesses);

            // Fetch Expenses
            const { data: expensesData, error: expensesError } = await supabase.from("legal_expenses").select("*").eq("user_id", user.id).order("date", { ascending: false });
            if (expensesError) throw expensesError;
            setExpenses(expensesData || []);

            // Fetch Deadlines (Calendar Events)
            const { data: deadlinesData, error: deadlinesError } = await supabase
                .from("legal_deadlines")
                .select("*")
                .eq("user_id", user.id)
                .order("due_date", { ascending: true });
            if (deadlinesError && deadlinesError.code !== '42P01') {
                console.error("Error fetching deadlines:", deadlinesError);
            }
            setDeadlines(deadlinesData || []);


            // Calculate Metrics (including calendar deadlines)
            const urgentTramitesCount = (tramitesData || []).filter(t => t.status !== 'completed' && t.status !== 'cancelled' && isUrgentDueDate(t.due_date)).length;
            const urgentDeadlinesCount = (deadlinesData || []).filter(d => d.status !== 'completed' && isUrgentDueDate(d.due_date)).length;

            setDashboardMetrics({
                activeProcesses: mappedProcesses.filter(p => p.status !== 'paused' && p.status !== 'archived').length || 0,
                totalClients: clientsData?.length || 0,
                pendingTramites: tramitesData?.filter(t => t.status === 'pending' || t.status === 'in_progress').length || 0,
                urgentTasksCount: urgentTramitesCount + urgentDeadlinesCount,
                recentActivity: 0
            });

        } catch (error: any) {
            console.error("Error fetching despachos data:", error);
            toast.error("Error cargando datos", {
                description: "No se pudieron obtener los datos actualizados.",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        fetchFiles();
    }, [user]);

    // Clients
    const addClient = async (client: Omit<Client, "id" | "created_at">) => {
        try {
            const { data, error } = await supabase.from("despachos_clients").insert([{ ...client, user_id: user?.id }]).select().single();
            if (error) throw error;
            setClients([data, ...clients]);
            setDashboardMetrics(prev => ({ ...prev, totalClients: prev.totalClients + 1 }));
            toast.success("Cliente creado exitosamente");
            return data;
        } catch (e: any) {
            toast.error("Error al crear cliente", { description: e.message });
            return null;
        }
    };

    const updateClient = async (id: string, updates: Partial<Client>) => {
        try {
            const { data, error } = await supabase.from("despachos_clients").update(updates).eq("id", id).select().single();
            if (error) throw error;
            setClients(clients.map(c => c.id === id ? data : c));
            toast.success("Cliente actualizado");
        } catch (e: any) {
            toast.error("Error al actualizar", { description: e.message });
        }
    };

    const deleteClient = async (id: string) => {
        try {
            const { error } = await supabase.from("despachos_clients").delete().eq("id", id);
            if (error) throw error;
            setClients(clients.filter(c => c.id !== id));
            setDashboardMetrics(prev => ({ ...prev, totalClients: prev.totalClients - 1 }));
            toast.success("Cliente eliminado");
        } catch (e: any) {
            toast.error("Error al eliminar", { description: e.message });
        }
    }

    // Contact Actions (NEW)
    const addContact = async (contact: Omit<Contact, "id">) => {
        try {
            const { data, error } = await supabase.from("despachos_contacts").insert([{ ...contact, user_id: user?.id }]).select().single();
            if (error) throw error;
            setContacts([data, ...contacts]);
            toast.success("Contacto creado", { description: `${contact.name} añadido a la agenda.` });
            return data;
        } catch (e: any) {
            toast.error("Error", { description: e.message });
        }
    };

    const deleteContact = async (id: string) => {
        try {
            const { error } = await supabase.from("despachos_contacts").delete().eq("id", id);
            if (error) throw error;
            setContacts(contacts.filter(c => c.id !== id));
            toast.success("Contacto eliminado");
        } catch (e: any) {
            toast.error("Error", { description: e.message });
        }
    }

    // Tramites
    const addTramite = async (tramite: Omit<Tramite, "id">) => {
        try {
            const { data, error } = await supabase.from("despachos_tramites").insert([{ ...tramite, user_id: user?.id }]).select().single();
            if (error) throw error;
            setTramites([...tramites, data]);
            setDashboardMetrics(prev => ({ ...prev, pendingTramites: prev.pendingTramites + 1 }));
            toast.success("Trámite registrado");
            return data;
        } catch (e: any) {
            toast.error("Error", { description: e.message });
        }
    }

    const updateTramite = async (id: string, updates: Partial<Tramite>) => {
        try {
            const { data, error } = await supabase.from("despachos_tramites").update(updates).eq("id", id).select().single();
            if (error) throw error;
            setTramites(tramites.map(t => t.id === id ? data : t));
            toast.success("Trámite actualizado");
        } catch (e: any) {
            toast.error("Error", { description: e.message });
        }
    }

    // Processes (procesos)
    const addProcess = async (process: any) => {
        try {
            // First check if a process with this case number already exists
            const { data: existing } = await supabase
                .from("procesos")
                .select("*")
                .eq("numero_proceso", process.case_number)
                .maybeSingle();

            const existingMeta = existing?.metadata || {};
            const rawIncomingTags = Array.isArray(process.tags) ? process.tags : [];
            const rawIncomingGrupos = Array.isArray(process.grupos) ? process.grupos : [];
            const rawExistingTags = Array.isArray(existingMeta.tags) ? existingMeta.tags : [];
            const rawExistingGrupos = Array.isArray(existingMeta.grupos) ? existingMeta.grupos : [];
            const unifiedNewTags = Array.from(new Set([
                ...rawIncomingTags,
                ...rawIncomingGrupos,
                ...rawExistingTags,
                ...rawExistingGrupos
            ])).filter((t: any) => typeof t === "string" && t.trim() !== "");

            const mergedMetadata = {
                ...existingMeta,
                client_name: process.client_name || existingMeta.client_name || '',
                client_email: process.client_email || existingMeta.client_email || '',
                client_phone: process.client_phone || existingMeta.client_phone || '',
                client_role: process.client_role || existingMeta.client_role || 'Demandante',
                description: process.description || existingMeta.description || '',
                tags: unifiedNewTags,
                grupos: unifiedNewTags,
                juzgado: process.juzgado || existingMeta.juzgado || '',
                ciudad: process.ciudad || existingMeta.ciudad || '',
                demandante: process.demandante || existingMeta.demandante || '',
                demandado: process.demandado || existingMeta.demandado || '',
                jurisdiccion: process.jurisdiccion || existingMeta.jurisdiccion || '',
                cuantia: process.cuantia || existingMeta.cuantia || '',
                fecha_inicio: process.fecha_inicio || existingMeta.fecha_inicio || '',
                fecha_radicacion: process.fecha_radicacion || existingMeta.fecha_radicacion || '',
                instancia: process.instancia || existingMeta.instancia || 'primera',
                ponente: process.ponente || existingMeta.ponente || '',
                clase: process.clase || existingMeta.clase || '',
                subclase: process.subclase || existingMeta.subclase || '',
                recurso: process.recurso || existingMeta.recurso || '',
                naturaleza: process.naturaleza || existingMeta.naturaleza || '',
                medida_cautelar: process.medida_cautelar || existingMeta.medida_cautelar || ''
            };

            const newProceso = {
                numero_proceso: process.case_number,
                asunto: process.title || existing?.asunto || '',
                clase: process.clase || existing?.clase || process.title || '',
                watch_status: process.status || existing?.watch_status || 'pending',
                ubicacion: process.juzgado || existing?.ubicacion || '',
                tipo_proceso: process.case_type || process.tipo_proceso || existing?.tipo_proceso || '',
                ponente: process.ponente || existing?.ponente || '',
                is_watched: true,
                user_id: user?.id,
                metadata: mergedMetadata,
            };

            const { data, error } = await supabase
                .from("procesos")
                .upsert([newProceso], { onConflict: 'numero_proceso' })
                .select()
                .single();

            if (error) throw error;

            const mapped = {
                id: data.id,
                case_number: data.numero_proceso,
                title: data.asunto || data.clase || 'Proceso sin título',
                description: data.metadata?.description || data.tipo_proceso || '',
                status: data.watch_status || (data.is_watched ? 'active' : 'inactive'),
                client_name: data.metadata?.client_name || '',
                client_email: data.metadata?.client_email || '',
                client_phone: data.metadata?.client_phone || '',
                client_role: data.metadata?.client_role || 'Demandante',
                updated_at: data.last_check_at || data.last_sync_at || data.created_at,
                radicado: data.numero_proceso,
                juzgado: data.ubicacion || '',
                ciudad: data.metadata?.ciudad || '',
                demandante: data.metadata?.demandante || '',
                demandado: data.metadata?.demandado || '',
                tags: data.metadata?.tags || [],
                user_id: data.user_id,
                ponente: data.ponente || data.metadata?.ponente || '',
                tipo_proceso: data.tipo_proceso || '',
                clase: data.clase || '',
                last_change_at: data.last_change_at || ''
            };

            // Remove previous one with same case_number if we are overriding it in list
            const filteredProcesses = processes.filter(p => p.case_number !== process.case_number);
            setProcesses([mapped, ...filteredProcesses]);
            setDashboardMetrics(prev => ({ ...prev, activeProcesses: prev.activeProcesses + 1 }));
            toast.success("Proceso registrado/sincronizado exitosamente");
            return mapped;
        } catch (e: any) {
            toast.error("Error al crear el proceso", { description: e.message });
            return null;
        }
    }

    const updateProcess = async (id: string, updates: any) => {
        if (!user) return;
        try {
            const { data: existing, error: fetchErr } = await supabase
                .from("procesos")
                .select("*")
                .eq("id", id)
                .single();

            if (fetchErr) throw fetchErr;

            const existingMeta = existing.metadata || {};

            // Consolidate updates for tags and grupos
            let nextTags = existingMeta.tags || existingMeta.grupos || [];
            if (updates.tags !== undefined || updates.grupos !== undefined) {
                const incoming = updates.tags !== undefined ? updates.tags : updates.grupos;
                nextTags = Array.isArray(incoming)
                    ? Array.from(new Set(incoming)).filter((t: any) => typeof t === "string" && t.trim() !== "")
                    : [];
            }

            const mergedMetadata = {
                ...existingMeta,
                client_name: updates.client_name !== undefined ? updates.client_name : existingMeta.client_name,
                client_email: updates.client_email !== undefined ? updates.client_email : existingMeta.client_email,
                client_phone: updates.client_phone !== undefined ? updates.client_phone : existingMeta.client_phone,
                client_role: updates.client_role !== undefined ? updates.client_role : existingMeta.client_role,
                description: updates.description !== undefined ? updates.description : existingMeta.description,
                tags: nextTags,
                grupos: nextTags,
                juzgado: updates.juzgado !== undefined ? updates.juzgado : existingMeta.juzgado,
                jurisdiccion: updates.jurisdiccion !== undefined ? updates.jurisdiccion : existingMeta.jurisdiccion,
                cuantia: updates.cuantia !== undefined ? updates.cuantia : existingMeta.cuantia,
                fecha_inicio: updates.fecha_inicio !== undefined ? updates.fecha_inicio : existingMeta.fecha_inicio,
                fecha_radicacion: updates.fecha_radicacion !== undefined ? updates.fecha_radicacion : existingMeta.fecha_radicacion,
                instancia: updates.instancia !== undefined ? updates.instancia : existingMeta.instancia,
                ponente: updates.ponente !== undefined ? updates.ponente : existingMeta.ponente,
                clase: updates.clase !== undefined ? updates.clase : existingMeta.clase,
                subclase: updates.subclase !== undefined ? updates.subclase : existingMeta.subclase,
                recurso: updates.recurso !== undefined ? updates.recurso : existingMeta.recurso,
                naturaleza: updates.naturaleza !== undefined ? updates.naturaleza : existingMeta.naturaleza,
                medida_cautelar: updates.medida_cautelar !== undefined ? updates.medida_cautelar : existingMeta.medida_cautelar
            };

            const dbUpdates: any = {
                metadata: mergedMetadata
            };

            if (updates.case_number !== undefined) dbUpdates.numero_proceso = updates.case_number;
            if (updates.title !== undefined) dbUpdates.asunto = updates.title;
            if (updates.status !== undefined) dbUpdates.watch_status = updates.status;
            if (updates.juzgado !== undefined) dbUpdates.ubicacion = updates.juzgado;
            if (updates.tipo_proceso !== undefined || updates.case_type !== undefined) dbUpdates.tipo_proceso = updates.tipo_proceso || updates.case_type;
            if (updates.clase !== undefined) dbUpdates.clase = updates.clase;
            if (updates.ponente !== undefined) dbUpdates.ponente = updates.ponente;

            const { error } = await supabase
                .from("procesos")
                .update(dbUpdates)
                .eq("id", id);

            if (error) throw error;

            setProcesses(processes.map(p => p.id === id ? { ...p, ...updates, tags: nextTags, grupos: nextTags } : p));
            toast.success("Proceso actualizado");
        } catch (e: any) {
            console.error(e);
            toast.error("Error al actualizar", { description: "No se pudo actualizar el proceso" });
        }
    };

    // Receipts
    const addReceipt = async (receipt: Omit<Receipt, "id">) => {
        try {
            const { data, error } = await supabase.from("despachos_receipts").insert([{ ...receipt, user_id: user?.id }]).select().single();
            if (error) throw error;
            setReceipts([data, ...receipts]);
            toast.success("Recibo generado");
            return data;
        } catch (e: any) {
            toast.error("Error", { description: e.message });
        }
    }


    // Tasks
    const addTask = async (task: any) => {
        try {
            const { data, error } = await supabase.from("legal_tasks").insert([{ ...task, user_id: user?.id }]).select().single();
            if (error) throw error;
            toast.success("Tarea creada");
            return data;
        } catch (e: any) {
            toast.error("Error", { description: e.message });
        }
    }

    // Events (Calendar)
    const addEvent = async (event: any) => {
        try {
            const { data, error } = await supabase.from("legal_deadlines").insert([{
                title: event.title,
                due_date: event.date || event.due_date,
                description: event.description,
                case_id: event.case_id || null,
                user_id: user?.id,
                status: event.status || 'pending'
            }]).select().single();
            if (error) throw error;
            setDeadlines(prev => [...prev, data]);
            toast.success("Evento agendado exitosamente");
            return data;
        } catch (e: any) {
            toast.error("Error al agendar evento", { description: e.message });
        }
    }

    const updateEvent = async (id: string, updates: any) => {
        try {
            const { data, error } = await supabase.from("legal_deadlines").update(updates).eq("id", id).select().single();
            if (error) throw error;
            setDeadlines(prev => prev.map(d => d.id === id ? data : d));
            toast.success("Evento actualizado");
            return data;
        } catch (e: any) {
            toast.error("Error", { description: e.message });
        }
    }

    const deleteEvent = async (id: string) => {
        try {
            const { error } = await supabase.from("legal_deadlines").delete().eq("id", id);
            if (error) throw error;
            setDeadlines(prev => prev.filter(d => d.id !== id));
            toast.success("Evento eliminado");
        } catch (e: any) {
            toast.error("Error", { description: e.message });
        }
    }

    // Expenses
    const addExpense = async (expense: any) => {
        try {
            const { data, error } = await supabase.from("legal_expenses").insert([{ ...expense, user_id: user?.id }]).select().single();
            if (error) throw error;
            setExpenses([...expenses, data]);
            toast.success("Gasto registrado");
            return data;
        } catch (e: any) {
            toast.error("Error", { description: e.message });
        }
    }

    const [files, setFiles] = useState<any[]>([]);

    const fetchFiles = async () => {
        if (!user) return;
        try {
            const { data, error } = await supabase.storage.from('gestion_misarchivos').list(user.id, {
                limit: 100,
                offset: 0,
                sortBy: { column: 'created_at', order: 'desc' },
            });
            if (error) {
                console.error("Error fetching files:", error);
            } else {
                setFiles(data || []);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const sanitizeFileName = (name: string): string => {
        let clean = name.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        clean = clean.replace(/[^a-zA-Z0-9\s._-]/g, "");
        return clean;
    };

    // Files
    const uploadFile = async (file: File, customName?: string) => {
        if (!user) return;
        setLoading(true);
        try {
            const fileName = customName || file.name;
            const sanitizedName = sanitizeFileName(fileName);
            const filePath = `${user.id}/${sanitizedName}`;

            const { error: uploadError } = await supabase.storage
                .from('gestion_misarchivos')
                .upload(filePath, file, {
                    upsert: true // Allow overwriting if the file exists
                });

            if (uploadError) throw uploadError;

            toast.success("Archivo subido", {
                description: `El archivo ${sanitizedName} se ha subido correctamente.`,
            });

            await fetchFiles(); // Refresh list
        } catch (error: any) {
            console.error("Error uploading file:", error);
            toast.error("Error al subir", {
                description: error.message,
            });
        } finally {
            setLoading(false);
        }
    };

    const deleteFile = async (fileName: string) => {
        if (!user) return;
        setLoading(true);
        try {
            const filePath = `${user.id}/${fileName}`;
            const { error } = await supabase.storage
                .from('gestion_misarchivos')
                .remove([filePath]);
            
            if (error) throw error;

            toast.success("Archivo eliminado", {
                description: `El archivo ${fileName} fue eliminado correctamente.`
            });
            await fetchFiles();
        } catch (e: any) {
            console.error("Error deleting file:", e);
            toast.error("Error al eliminar archivo", {
                description: e.message
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user) fetchFiles();
    }, [user]);

    return {
        deleteFile,
        clients,
        contacts,
        tramites,
        receipts,
        processes,
        expenses,
        files,
        dashboardMetrics,
        loading,
        deadlines,
        urgentTasks: [
            ...(tramites || [])
                .filter(t => t.status !== 'completed' && t.status !== 'cancelled' && isUrgentDueDate(t.due_date))
                .map(t => ({
                    id: t.id,
                    title: t.title,
                    due_date: t.due_date,
                    status: t.status,
                    type: 'tramite',
                    description: t.description || t.entity || ''
                })),
            ...(deadlines || [])
                .filter(d => d.status !== 'completed' && isUrgentDueDate(d.due_date))
                .map(d => ({
                    id: d.id,
                    title: `📅 ${d.title}`,
                    due_date: d.due_date,
                    status: d.status,
                    type: 'calendar',
                    description: d.description || 'Término judicial del calendario'
                }))
        ],
        refresh: fetchData,
        addClient,
        updateClient,
        deleteClient,
        addContact,
        deleteContact,
        addTramite,
        updateTramite,
        addProcess,
        updateProcess,
        archiveProcess: async (id: string) => {
            try {
                const { error } = await supabase
                    .from("procesos")
                    .update({
                        watch_status: "archived",
                    })
                    .eq("id", id);
                if (error) throw error;
                setProcesses(processes.map(p => p.id === id ? { ...p, status: "archived", updated_at: new Date().toISOString() } : p));
                toast.success("Proceso archivado");
                return true;
        } catch (e: any) {
                toast.error("Error al archivar", { description: e.message });
                return false;
        }
        },
        deleteProcess: async (id: string) => {
            try {
                const target = processes.find(p => p.id === id);
                if (target) {
                    try {
                        const { deleteScrapperProceso } = await import("@/services/scraperApi");
                        await deleteScrapperProceso(target.case_number);
                    } catch (apiErr) {
                        console.error("Failed to delete process from scraper backend:", apiErr);
                    }
                }
                const { error } = await supabase.from("procesos").delete().eq("id", id);
                if (error) throw error;
                setProcesses(processes.filter(p => p.id !== id));
                setDashboardMetrics(prev => ({ ...prev, activeProcesses: prev.activeProcesses - 1 }));
                toast.success("Proceso eliminado");
                return true;
            } catch (e: any) {
                toast.error("Error al eliminar", { description: e.message });
                return false;
            }
        },
        addReceipt,
        addTask,
        addEvent,
        updateEvent,
        deleteEvent,
        addExpense,
        uploadFile,
        fetchFiles,
        uploadProcessFile: async (_file: File, _processId: string) => {},
        refetch: fetchData,
    };
}
